/**
 * Cognito browser client (Slice C3 — Option A: the SPA authenticates directly
 * against Amazon Cognito).
 *
 * The SPA no longer calls a custom Auth_Service. Instead it uses the AWS-
 * published `amazon-cognito-identity-js` SDK to run the `USER_PASSWORD_AUTH`
 * flow straight from the browser against the Cognito User Pool. MFA is ON on
 * the pool using a TOTP software token (authenticator app), so sign-in is TWO
 * steps: email + password first, then the current 6-digit code from the user's
 * authenticator app. The password step surfaces a `SOFTWARE_TOKEN_MFA`
 * challenge and the code step completes it. No SMS is involved.
 *
 * Token model: the per-service API Gateway JWT authorizer validates the Cognito
 * **idToken** (it checks `aud` = the app client id; access tokens carry no
 * `aud`). The SPA therefore uses the **idToken** as the `Authorization: Bearer`
 * credential — see {@link ./tokenStore} and {@link ../api/client}. The verified
 * `sub` claim is the customer id; services read identity from the authorizer-
 * mapped `x-customer-id`, so the SPA must not send that header itself.
 *
 * Persistence note: `amazon-cognito-identity-js` persists tokens in
 * `localStorage` by default (keyed under `CognitoIdentityServiceProvider.*`).
 * That is acceptable for this demo; {@link getSession} relies on it to restore
 * a session across reloads, replacing the old silent `/auth/refresh` call.
 */

import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';

import { ApiError } from './errors.js';

/**
 * Pool configuration, read from Vite build-time env with the live deployed
 * values baked in as defaults so the SPA works even when the env vars are not
 * set at build time.
 */
const USER_POOL_ID: string =
  (import.meta.env.VITE_COGNITO_USER_POOL_ID as string | undefined) ??
  'af-south-1_PCKfz7Qyu';
const CLIENT_ID: string =
  (import.meta.env.VITE_COGNITO_CLIENT_ID as string | undefined) ??
  '6685fr61l8guu2b78kqdoii00';
// Region is derived from the pool id (`{region}_{poolSuffix}`); the explicit
// env var (default 'af-south-1') is retained for clarity/future use.
const REGION: string =
  (import.meta.env.VITE_COGNITO_REGION as string | undefined) ?? 'af-south-1';
void REGION;

/** The established-session shape returned by {@link signInWithPassword}/{@link getSession}. */
export interface CognitoSession {
  /** The Cognito **idToken** JWT — the credential the API Gateway accepts. */
  readonly idToken: string;
  /** The Cognito access token JWT (kept for completeness; not sent as Bearer). */
  readonly accessToken: string;
  /** The verified `sub` claim — the authenticated customer id. */
  readonly customerId: string;
  /**
   * A human-friendly label for the signed-in user, derived from the idToken
   * claims: the `name` claim when present, else `email`, else a shortened
   * form of the `sub`. Used for display only (never for authorization).
   */
  readonly displayName: string;
  /** The idToken expiry in epoch milliseconds (`exp * 1000`). */
  readonly expiresAt: number;
}

/**
 * The discriminated result of the password step ({@link signInWithPassword}).
 *
 * - `session` — the account has no MFA (or it was auto-satisfied) and tokens
 *   were issued directly; the flow is complete.
 * - `mfa` — Cognito issued a TOTP (`SOFTWARE_TOKEN_MFA`) challenge. The caller
 *   must collect the current 6-digit code from the user's authenticator app and
 *   complete the flow via {@link submitTotpOtp}.
 */
export type SignInResult =
  | { kind: 'session'; session: CognitoSession }
  | { kind: 'mfa' };

/** Lazily-constructed pool singleton (one per loaded SPA instance). */
let poolInstance: CognitoUserPool | undefined;

/**
 * The {@link CognitoUser} carried between the password step and the TOTP code
 * step. `amazon-cognito-identity-js` completes a TOTP MFA challenge by calling
 * `sendMFACode(code, callbacks, 'SOFTWARE_TOKEN_MFA')` on the SAME
 * `CognitoUser` instance that raised `totpRequired`, so it must be held across
 * the two calls. This is a per-loaded-SPA singleton (one in-flight sign-in at a
 * time); it is cleared on success, on a terminal failure, and on
 * {@link cancelTotpOtp}.
 */
let pendingMfaUser: CognitoUser | undefined;

function getPool(): CognitoUserPool {
  if (poolInstance === undefined) {
    poolInstance = new CognitoUserPool({
      UserPoolId: USER_POOL_ID,
      ClientId: CLIENT_ID,
    });
  }
  return poolInstance;
}

/** Extract a {@link CognitoSession} from a resolved Cognito session object. */
function toSession(session: CognitoUserSession): CognitoSession {
  const idToken = session.getIdToken();
  const jwt = idToken.getJwtToken();
  const payload = idToken.payload as {
    sub?: unknown;
    exp?: unknown;
    email?: unknown;
    name?: unknown;
  };
  const customerId = typeof payload.sub === 'string' ? payload.sub : '';
  const expSeconds = typeof payload.exp === 'number' ? payload.exp : 0;
  // Human-friendly display label: prefer a real name, then email, then a
  // shortened sub so we never surface a raw UUID to the customer.
  const name = typeof payload.name === 'string' ? payload.name.trim() : '';
  const email = typeof payload.email === 'string' ? payload.email.trim() : '';
  const displayName =
    name.length > 0
      ? name
      : email.length > 0
        ? email
        : customerId.slice(0, 8);
  return {
    idToken: jwt,
    accessToken: session.getAccessToken().getJwtToken(),
    customerId,
    displayName,
    expiresAt: expSeconds * 1000,
  };
}

/**
 * Sign in with email + password using the `USER_PASSWORD_AUTH` flow. The pool
 * has TOTP MFA ON, so this is the FIRST of two steps and resolves a
 * {@link SignInResult}:
 *
 * - `{ kind: 'session', session }` — no MFA challenge was raised; tokens were
 *   issued directly and the flow is complete.
 * - `{ kind: 'mfa' }` — Cognito raised a TOTP (`SOFTWARE_TOKEN_MFA`) challenge;
 *   complete the flow via {@link submitTotpOtp} with the current authenticator
 *   app code.
 *
 * Rejects with an {@link ApiError} carrying a friendly, non-sensitive message
 * for the common failure paths.
 */
export function signInWithPassword(
  email: string,
  password: string,
): Promise<SignInResult> {
  return runPasswordAuth(email, password);
}

/**
 * Runs the server-side `USER_PASSWORD_AUTH` password flow and resolves the
 * {@link SignInResult} discriminated union, keeping the single-in-flight
 * {@link pendingMfaUser} correct on every branch.
 */
function runPasswordAuth(email: string, password: string): Promise<SignInResult> {
  return new Promise<SignInResult>((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: getPool() });
    // Server-side password auth (no SRP); the pool client has USER_PASSWORD_AUTH
    // enabled. MFA is ON with a TOTP software token, so a successful password
    // check may raise a `SOFTWARE_TOKEN_MFA` challenge (`totpRequired`) instead
    // of returning tokens immediately.
    user.setAuthenticationFlowType('USER_PASSWORD_AUTH');

    const details = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    user.authenticateUser(details, {
      onSuccess: (session) => {
        pendingMfaUser = undefined;
        resolve({ kind: 'session', session: toSession(session) });
      },
      onFailure: (err: unknown) => {
        pendingMfaUser = undefined;
        reject(mapAuthError(err));
      },
      totpRequired: (_challengeName: unknown, _challengeParameters?: unknown) => {
        // Password accepted; Cognito wants the current TOTP code from the
        // user's authenticator app. Keep this exact CognitoUser instance so
        // submitTotpOtp can complete the same challenge.
        pendingMfaUser = user;
        resolve({ kind: 'mfa' });
      },
      newPasswordRequired: () => {
        // The account is in FORCE_CHANGE_PASSWORD. The demo user's password is
        // already permanent, so this should not trigger; handle it gracefully
        // rather than leaving the promise pending.
        pendingMfaUser = undefined;
        reject(
          new ApiError(403, {
            code: 'ACCOUNT_LOCKED',
            message:
              'A password change is required for this account. Please contact support to complete setup.',
          }),
        );
      },
    });
  });
}

/**
 * Complete the TOTP MFA challenge raised by {@link signInWithPassword} by
 * submitting the current 6-digit code from the user's authenticator app. Must
 * be called after a `{ kind: 'mfa' }` result and before the in-flight challenge
 * is abandoned.
 *
 * Resolves with the established {@link CognitoSession} on success. Rejects with
 * an {@link ApiError} on failure:
 * - a wrong code (`INVALID_OTP`) keeps the pending challenge so the user can
 *   retry immediately with the next rotating code;
 * - an expired/invalid session (`AUTH_REQUIRED`) clears it, so the caller
 *   should send the user back to the password step.
 * If no challenge is pending, rejects with `AUTH_REQUIRED`.
 */
export function submitTotpOtp(code: string): Promise<CognitoSession> {
  return new Promise<CognitoSession>((resolve, reject) => {
    if (pendingMfaUser === undefined) {
      reject(
        new ApiError(400, {
          code: 'AUTH_REQUIRED',
          message: 'Your sign-in session expired. Please sign in again.',
        }),
      );
      return;
    }
    pendingMfaUser.sendMFACode(
      code.trim(),
      {
        onSuccess: (session) => {
          pendingMfaUser = undefined;
          resolve(toSession(session));
        },
        onFailure: (err: unknown) => {
          const mapped = mapMfaError(err);
          if (mapped.code === 'AUTH_REQUIRED') {
            pendingMfaUser = undefined;
          }
          reject(mapped);
        },
      },
      'SOFTWARE_TOKEN_MFA',
    );
  });
}

/**
 * Abandon an in-flight TOTP MFA challenge (e.g. the user chose "Back to sign
 * in"). Clears the pending {@link CognitoUser} so the next sign-in starts
 * cleanly.
 */
export function cancelTotpOtp(): void {
  pendingMfaUser = undefined;
}

/**
 * Restore a session on reload from the SDK's `localStorage`-persisted tokens.
 * Resolves with a {@link CognitoSession} when a valid (non-expired, refreshable)
 * session exists, or `null` when signed out / the session cannot be restored.
 */
export function getSession(): Promise<CognitoSession | null> {
  return new Promise<CognitoSession | null>((resolve) => {
    const user = getPool().getCurrentUser();
    if (user === null) {
      resolve(null);
      return;
    }
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err !== null || session === null || !session.isValid()) {
        resolve(null);
        return;
      }
      resolve(toSession(session));
    });
  });
}

/** Sign out the current user, clearing the SDK's persisted tokens. */
export function signOutCognito(): void {
  getPool().getCurrentUser()?.signOut();
}

/** Map a Cognito SDK error to a friendly {@link ApiError}. */
function mapAuthError(err: unknown): ApiError {
  const name =
    typeof err === 'object' && err !== null && 'name' in err
      ? String((err as { name?: unknown }).name)
      : '';

  if (name === 'NotAuthorizedException' || name === 'UserNotFoundException') {
    // Same message for both so account existence cannot be inferred.
    return new ApiError(401, {
      code: 'INVALID_CREDENTIALS',
      message: 'Incorrect email or password. Please try again.',
    });
  }
  if (name === 'PasswordResetRequiredException') {
    return new ApiError(403, {
      code: 'ACCOUNT_LOCKED',
      message: 'Your password must be reset. Please contact support.',
    });
  }
  if (name === 'UserNotConfirmedException') {
    return new ApiError(403, {
      code: 'ACCOUNT_LOCKED',
      message: 'This account is not yet confirmed. Please contact support.',
    });
  }
  if (name === 'TooManyRequestsException' || name === 'LimitExceededException') {
    return new ApiError(429, {
      code: 'SERVICE_UNAVAILABLE',
      message: 'Too many attempts. Please wait a moment and try again.',
    });
  }
  return new ApiError(0, {
    code: 'SERVICE_UNAVAILABLE',
    message: 'Unable to reach the sign-in service. Check your connection and try again.',
  });
}

/**
 * Map a Cognito TOTP-MFA (`sendMFACode` with `SOFTWARE_TOKEN_MFA`) error to a
 * friendly {@link ApiError}. Distinguishes a retryable wrong code
 * (`INVALID_OTP`) from a terminal expired/invalid session (`AUTH_REQUIRED`) so
 * the caller can decide whether to keep the user on the code step or send them
 * back to the password step. TOTP codes rotate every 30s, so a rejected code is
 * treated as retryable — the user simply enters the next code from their app.
 */
function mapMfaError(err: unknown): ApiError {
  const name =
    typeof err === 'object' && err !== null && 'name' in err
      ? String((err as { name?: unknown }).name)
      : '';

  if (name === 'CodeMismatchException' || name === 'ExpiredCodeException') {
    return new ApiError(401, {
      code: 'INVALID_OTP',
      message: 'That code is incorrect or has expired. Enter the current code from your authenticator app.',
    });
  }
  if (name === 'NotAuthorizedException') {
    return new ApiError(401, {
      code: 'AUTH_REQUIRED',
      message: 'Your sign-in session expired. Please sign in again.',
    });
  }
  return new ApiError(0, {
    code: 'SERVICE_UNAVAILABLE',
    message: 'Unable to reach the sign-in service. Check your connection and try again.',
  });
}
